// ############################################################
// Copyright (C) 2021-2025 by Gamify Software Inc - All Rights Reserved
// You may NOT distribute or modify this code without the expressed
// written consent of Gamify Software Inc.
// #############################################################

import * as React from 'react';
import {
  ImagePlus,
  MessageSquare,
  FileText,
  Building2,
  Contact,
  ListChecks,
  Calendar,
  Star,
  ShieldCheck,
  Loader2,
  Upload,
  Trash2,
  Download,
  X,
  CloudRain,
  Pencil,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';
import { ConfirmModal } from '../ui/confirm-modal';
import featureFlag from '../../../application/common/featureFlag';
import { getDealOwnerConfig } from '../../lib/getDealOwnerConfig';
import {
  DealComment,
  DealAppointment,
  DealPhoto,
} from '../../hooks/use-deal-detail';
import { useToast } from '../ui/toast';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import * as mutations from '../../../graphql/mutations';
import {
  getTZ,
  generateRandomImageFileNameString,
  newLocalToISODateFormat,
  getLocalDateTime,
} from '../../../application/common/Utilities';
import Dropzone from 'react-dropzone';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import PhotoAnnotator from './PhotoAnnotator';

// ── Tab order config (mirrors mobile tabOrderConfig.ts) ─────

const DEFAULT_TAB_ORDER = {
  aiInspection: [
    'Photos',
    'Claim',
    'Contracts',
    'Notes',
    'Lead',
    'Door data',
    'Appointments',
    'Reviews',
  ],
  standard: [
    'Door data',
    'Notes',
    'Status',
    'Appointments',
    'Lead',
    'Contracts',
    'Photos',
    'Claim',
    'Reviews',
  ],
};

type TabOrderMode = 'aiInspection' | 'standard';

function getTabOrder(mode: TabOrderMode): string[] {
  const orgConfig = (global as any)?.appSettings?.tabOrder;
  if (orgConfig?.[mode]?.length) {
    return orgConfig[mode];
  }
  return DEFAULT_TAB_ORDER[mode];
}

function sortTabsByOrder(order: string[], visible: string[]): string[] {
  const sorted: string[] = [];
  const remaining = [...visible];

  for (const label of order) {
    const idx = remaining.indexOf(label);
    if (idx !== -1) {
      sorted.push(label);
      remaining.splice(idx, 1);
    }
  }
  sorted.push(...remaining);
  return sorted;
}

// ── Types ───────────────────────────────────────────────────

interface TabDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
  content: React.ReactNode;
}

export interface DealTabbedContentProps {
  deal: any;
  dealId: string;
  comments?: DealComment[];
  photos?: DealPhoto[];
  appointments?: DealAppointment[];
  categoryId?: string;
  isReadOnly?: boolean;
  showEdit?: boolean;
  pollTick?: number;
  onTriggerSelectedDealRefetch?: () => void;
  handleRefetchDeals?: (...args: any[]) => void;
  onStagePress?: () => void;
  /** When provided, selecting an inspection opens the chat in a side panel */
  onAITabClick?: (dealId: string, taskListId: string) => void;
}

// ── Tab builder (mirrors v1 buildTabs logic) ────────────────

function buildTabs(props: DealTabbedContentProps): TabDef[] {
  const {
    deal,
    dealId,
    comments = [],
    photos = [],
    appointments = [],
    pollTick,
    categoryId,
    isReadOnly = false,
    showEdit = true,
    onTriggerSelectedDealRefetch,
    handleRefetchDeals,
  } = props;

  const tabs: TabDef[] = [];
  const isAI = featureFlag('AIInspection');

  // Photos tab
  tabs.push({
    id: 'photos',
    label: 'Photos',
    icon: <ImagePlus className="size-3.5" />,
    content: (
      <DealPhotosTab
        photos={photos}
        deal={deal}
        showEdit={showEdit && !isReadOnly}
        onTriggerSelectedDealRefetch={onTriggerSelectedDealRefetch}
        handleRefetchDeals={handleRefetchDeals}
      />
    ),
  });

  // Claim tab
  tabs.push({
    id: 'claim',
    label: 'Claim',
    icon: <ShieldCheck className="size-3.5" />,
    content: (
      <DealClaimTab
        dealId={dealId}
        deal={deal}
        pollTick={pollTick}
        onOpenChat={props.onAITabClick}
      />
    ),
  });

  // Contracts tab
  if (
    (global as any)?.appSettings?.features?.deal?.signOnDevice ||
    (global as any)?.appSettings?.features?.deal?.signUsingEmail
  ) {
    tabs.push({
      id: 'contracts',
      label: 'Contracts',
      icon: <FileText className="size-3.5" />,
      content: <DealContractsTab deal={deal} />,
    });
  }

  // Notes tab
  tabs.push({
    id: 'notes',
    label: 'Notes',
    icon: <MessageSquare className="size-3.5" />,
    badge: comments.length || undefined,
    content: (
      <DealNotesTab
        comments={comments}
        dealId={dealId}
        categoryId={categoryId}
        currentOwnerID={deal?.currentOwnerID}
        showEdit={showEdit && !isReadOnly}
        onRefetch={onTriggerSelectedDealRefetch}
      />
    ),
  });

  // Lead tab
  tabs.push({
    id: 'lead',
    label: 'Lead',
    icon: <Contact className="size-3.5" />,
    content: <DealLeadInfoTab deal={deal} />,
  });

  // Door data tab
  tabs.push({
    id: 'door-data',
    label: 'Door data',
    icon: <Building2 className="size-3.5" />,
    content: <DealDoorDataTab deal={deal} />,
  });

  // Status tab (standard mode only)
  if (!isAI) {
    tabs.push({
      id: 'status',
      label: 'Status',
      icon: <ListChecks className="size-3.5" />,
      content: <DealStatusTab deal={deal} onStagePress={props.onStagePress} />,
    });
  }

  // Appointments tab
  if ((global as any)?.appSettings?.features?.schedule?.enabled !== false) {
    tabs.push({
      id: 'appointments',
      label: 'Appointments',
      icon: <Calendar className="size-3.5" />,
      content: <DealAppointmentsTab appointments={appointments} />,
    });
  }

  // Reviews tab
  if (featureFlag('reviewRequest')) {
    tabs.push({
      id: 'reviews',
      label: 'Reviews',
      icon: <Star className="size-3.5" />,
      content: <DealReviewsTab />,
    });
  }

  // Sort by org tab order config
  const mode: TabOrderMode = isAI ? 'aiInspection' : 'standard';
  const order = getTabOrder(mode);
  const visibleLabels = tabs.map((t) => t.label);
  const sortedLabels = sortTabsByOrder(order, visibleLabels);

  const sorted: TabDef[] = [];
  const remaining = [...tabs];
  for (const label of sortedLabels) {
    const idx = remaining.findIndex((t) => t.label === label);
    if (idx !== -1) {
      sorted.push(remaining[idx]);
      remaining.splice(idx, 1);
    }
  }
  // Append any tabs not in the order config
  sorted.push(...remaining);
  return sorted;
}

// ── Component ───────────────────────────────────────────────

const DealTabbedContent: React.FC<DealTabbedContentProps> = (props) => {
  const [activeTab, setActiveTab] = React.useState('');

  const tabs = React.useMemo(
    () => buildTabs(props),
    [
      props.deal,
      props.dealId,
      props.comments,
      props.photos,
      props.appointments,
      props.pollTick,
      props.showEdit,
      props.isReadOnly,
    ]
  );

  // Set initial tab when tabs are built
  React.useEffect(() => {
    if (tabs.length > 0 && !tabs.find((t) => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  const tabTriggerClass =
    'rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary px-4 pb-3 pt-2 text-sm font-bold whitespace-nowrap gap-1.5 text-muted-foreground';

  const safeActiveTab =
    tabs.find((t) => t.id === activeTab)?.id || tabs[0]?.id || '';

  return (
    <div className="w-full overflow-hidden">
      <Tabs value={safeActiveTab} onValueChange={setActiveTab}>
        <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start h-auto p-0 gap-0 overflow-x-auto overflow-y-hidden flex-nowrap">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className={tabTriggerClass}
            >
              {tab.icon}
              {tab.label}
              {tab.badge && tab.badge > 0 && (
                <span className="ml-1 text-[10px] bg-muted rounded-full px-1.5 py-0.5 font-medium">
                  {tab.badge}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {tabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id} className="mt-4">
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default React.memo(DealTabbedContent);
export { buildTabs, getTabOrder, sortTabsByOrder };
export type { TabDef };

// ── Tab content components ──────────────────────────────────

function formatScheduleDate(date: string, minutesPastMidnight: number): string {
  const hours = Math.trunc(minutesPastMidnight / 60);
  const minutes = minutesPastMidnight - hours * 60;
  const d = new Date(
    `${date}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0'
    )}:00`
  );
  if (isNaN(d.getTime())) {
    return date;
  }
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function DealPhotosTab({
  photos,
  deal,
  showEdit = false,
  onTriggerSelectedDealRefetch,
  handleRefetchDeals,
}: {
  photos: DealPhoto[];
  deal: any;
  showEdit?: boolean;
  onTriggerSelectedDealRefetch?: () => void;
  handleRefetchDeals?: (...args: any[]) => void;
}) {
  const { success: toastSuccess, error: toastError } = useToast();
  const [isUploading, setIsUploading] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [photoToDelete, setPhotoToDelete] = React.useState<DealPhoto | null>(
    null
  );
  const [lightboxIndex, setLightboxIndex] = React.useState<number | null>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [selectedTag, setSelectedTag] = React.useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
  const [videoUrl, setVideoUrl] = React.useState<string | null>(null);

  // Photo annotator state
  const [showPhotoAnnotator, setShowPhotoAnnotator] = React.useState(false);
  const [annotatorPhoto, setAnnotatorPhoto] = React.useState<{
    imageName: string;
    tags: string[];
    description: string;
    fileId?: string;
    type?: string;
  } | null>(null);

  const isPaivVideoEnabled = featureFlag('PaivVideo');

  const isImage = (photo: DealPhoto) => {
    const type = photo.type || '';
    const name = photo.imageName || '';
    if (type.startsWith('video/') || /\.(mp4|mov|avi|mkv)$/i.test(name)) {
      return false;
    }
    if (type === 'application/pdf' || /\.pdf$/i.test(name)) {
      return false;
    }
    if (type === 'application/json' || /\.json$/i.test(name)) {
      return false;
    }
    if (type === 'report') {
      return false;
    }
    return true;
  };

  const isReport = (photo: DealPhoto) => {
    const type = photo.type || '';
    const name = photo.imageName || '';
    return (
      type === 'report' || type === 'application/json' || /\.json$/i.test(name)
    );
  };

  const isPdf = (photo: DealPhoto) => {
    const type = photo.type || '';
    const name = photo.imageName || '';
    return type === 'application/pdf' || /\.pdf$/i.test(name);
  };

  const isVideo = (photo: DealPhoto) => {
    const type = photo.type || '';
    const name = photo.imageName || '';
    return type.startsWith('video/') || /\.(mp4|mov|avi|mkv)$/i.test(name);
  };

  // Extract all unique tags
  const allTags = React.useMemo(() => {
    return Array.from(new Set(photos.flatMap((p) => p.tags || []))).filter(
      (tag) => tag && tag.length > 0
    );
  }, [photos]);

  // Filter photos by selected tag
  const filteredPhotos = React.useMemo(() => {
    if (!selectedTag) {
      return photos;
    }
    return photos.filter((p) => p.tags && p.tags.includes(selectedTag));
  }, [photos, selectedTag]);

  const imagePhotos = filteredPhotos.filter((p) => isImage(p) && !isReport(p));
  const otherFiles = filteredPhotos.filter((p) => !isImage(p) && !isReport(p));

  const handleUpload = async (droppedFiles: File[]) => {
    if (!droppedFiles.length || !deal) {
      return;
    }
    setIsUploading(true);

    const baseFormats = ['jpeg', 'jpg', 'png', 'gif', 'pdf'];
    const videoFormats = ['mp4', 'mov', 'avi', 'mkv'];
    const formats = isPaivVideoEnabled
      ? [...baseFormats, ...videoFormats]
      : baseFormats;

    const file = droppedFiles[0];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';

    if (!formats.includes(ext)) {
      const supported = isPaivVideoEnabled
        ? 'png, gif, jpeg, pdf, mp4, mov, avi, mkv'
        : 'png, gif, jpeg, pdf';
      toastError(`File format must be: ${supported}`);
      setIsUploading(false);
      return;
    }

    try {
      const randomString = generateRandomImageFileNameString();
      const newFileName = `${randomString}.${ext}`;

      await Storage.put(newFileName, file, { contentType: file.type });

      const flexAttributes = JSON.parse(deal.flexAttributes || '{}');
      if (!flexAttributes.photo) {
        flexAttributes.photo = [];
      }
      if (!flexAttributes.pictures) {
        flexAttributes.pictures = [];
      }
      flexAttributes.photo = [newFileName, ...flexAttributes.photo];
      flexAttributes.pictures = [newFileName, ...flexAttributes.pictures];

      deal.flexAttributes = JSON.stringify(flexAttributes);

      const ourDeal = {
        id: deal.id,
        name: deal.name,
        status: deal.status,
        imageName: deal.imageName,
        imageType: deal.imageType,
        currentStageID: deal.currentStageID,
        currentOwnerID: deal.currentOwnerID,
        currentOwnerType: deal.currentOwnerType,
        orgID: deal.orgID,
        isDeleted: false,
        flexAttributes: deal.flexAttributes,
        categoryID: deal.categoryID,
        tmst: newLocalToISODateFormat(getLocalDateTime()),
      };

      await API.graphql(
        graphqlOperation(mutations.maintainDealV3, {
          dealInfo: JSON.stringify(ourDeal),
        })
      );

      toastSuccess('File uploaded successfully');

      setTimeout(() => {
        onTriggerSelectedDealRefetch?.();
        handleRefetchDeals?.();
      }, 1000);
    } catch (err) {
      console.error('Upload error:', err);
      toastError('Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (photo: DealPhoto) => {
    if (!deal) {
      return;
    }
    setDeletingId(photo.id);

    try {
      const flexAttributes = JSON.parse(deal.flexAttributes || '{}');
      if (!Array.isArray(flexAttributes.photo)) {
        flexAttributes.photo = [];
      }
      if (!Array.isArray(flexAttributes.pictures)) {
        flexAttributes.pictures = [];
      }

      const imageKey = photo.imageName;
      flexAttributes.photo = flexAttributes.photo.filter(
        (img: string) => img !== imageKey && img !== photo.id
      );
      flexAttributes.pictures = flexAttributes.pictures.filter(
        (img: string) => img !== imageKey && img !== photo.id
      );

      // Mark file as deleted if it has a real DealFile ID (from deal.files.items)
      // Photos from flexAttributes (flex-photo-*) don't have database records
      if (
        photo.id &&
        !photo.id.startsWith('photo-') &&
        !photo.id.startsWith('flex-photo-')
      ) {
        try {
          await API.graphql(
            graphqlOperation(mutations.updateDealFile, {
              input: { id: photo.id, isDeleted: true },
            })
          );
        } catch (updateErr) {
          console.error('Error marking file as deleted:', updateErr);
        }
      }

      deal.flexAttributes = JSON.stringify(flexAttributes);

      const ourDeal = {
        id: deal.id,
        name: deal.name,
        status: deal.status,
        imageName: deal.imageName,
        imageType: deal.imageType,
        currentStageID: deal.currentStageID,
        currentOwnerID: deal.currentOwnerID,
        currentOwnerType: deal.currentOwnerType,
        orgID: deal.orgID,
        isDeleted: false,
        flexAttributes: deal.flexAttributes,
        categoryID: deal.categoryID,
        tmst: newLocalToISODateFormat(getLocalDateTime()),
      };

      await API.graphql(
        graphqlOperation(mutations.maintainDealV3, {
          dealInfo: JSON.stringify(ourDeal),
        })
      );

      toastSuccess('File deleted successfully');

      setTimeout(() => {
        onTriggerSelectedDealRefetch?.();
        handleRefetchDeals?.();
      }, 1000);
    } catch (err) {
      console.error('Delete error:', err);
      toastError('Failed to delete file');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownloadAll = async () => {
    const validPhotos = photos.filter(
      (p) => p.imageName && p.imageName !== 'undefined' && !isReport(p)
    );
    if (validPhotos.length === 0) {
      toastError('No files to download');
      return;
    }

    setIsDownloading(true);
    try {
      const zip = new JSZip();
      let successCount = 0;

      for (const photo of validPhotos) {
        try {
          const signedUrl = await Storage.get(photo.imageName);
          const response = await fetch(signedUrl as string);
          const blob = await response.blob();
          const fileName =
            photo.imageName.split('/').pop() || `file-${photo.id}`;
          zip.file(fileName, blob);
          successCount++;
        } catch (err) {
          console.error(`Failed to fetch ${photo.imageName}:`, err);
        }
      }

      if (successCount === 0) {
        toastError('Failed to download files');
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const dealName = deal?.name || 'deal';
      const safeName = dealName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      saveAs(zipBlob, `${safeName}_files.zip`);

      toastSuccess(`Downloaded ${successCount} files`);
    } catch (error) {
      console.error('Error downloading files:', error);
      toastError('Failed to create zip file');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileClick = async (photo: DealPhoto, index: number) => {
    if (isPdf(photo)) {
      try {
        const signedUrl = await Storage.get(photo.imageName);
        setPdfUrl(signedUrl as string);
      } catch (err) {
        console.error('Error opening PDF:', err);
        toastError('Could not open PDF');
      }
    } else if (isPaivVideoEnabled && isVideo(photo)) {
      try {
        const signedUrl = await Storage.get(photo.imageName);
        setVideoUrl(signedUrl as string);
      } catch (err) {
        console.error('Error opening video:', err);
        toastError('Could not open video');
      }
    } else {
      setLightboxIndex(index);
    }
  };

  if (!photos.length && !showEdit) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <ImagePlus className="size-10 mb-2" />
        <p className="text-sm">No photos yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Upload dropzone */}
      {showEdit && (
        <Dropzone onDrop={handleUpload} multiple={false} disabled={isUploading}>
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div
              {...getRootProps()}
              className={cn(
                'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors',
                isDragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              )}
            >
              <input {...getInputProps()} />
              {isUploading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="size-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">
                    Uploading...
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Upload className="size-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Drop a file here or click to upload
                  </span>
                </div>
              )}
            </div>
          )}
        </Dropzone>
      )}

      {/* Action bar: tag filter + download all */}
      {(allTags.length > 0 || photos.length > 0) && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {allTags.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => setSelectedTag(null)}
                className={cn(
                  'text-xs px-2 py-0.5 rounded-full border transition-colors',
                  !selectedTag
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted text-muted-foreground border-border hover:border-primary/50'
                )}
              >
                All
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() =>
                    setSelectedTag(tag === selectedTag ? null : tag)
                  }
                  className={cn(
                    'text-xs px-2 py-0.5 rounded-full border transition-colors',
                    selectedTag === tag
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted text-muted-foreground border-border hover:border-primary/50'
                  )}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
          {photos.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-xs"
              onClick={handleDownloadAll}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Download className="size-3.5" />
              )}
              Download All
            </Button>
          )}
        </div>
      )}

      {/* Image grid */}
      {imagePhotos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-3">
          {imagePhotos.map((photo, idx) => (
            <div
              key={photo.id}
              className="aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer hover:opacity-90 transition-opacity relative group"
              onClick={() => handleFileClick(photo, idx)}
            >
              {photo.url ? (
                <img
                  src={photo.url}
                  alt={photo.description || 'Photo'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImagePlus className="size-6 text-muted-foreground" />
                </div>
              )}
              {photo.description && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-[10px] text-white truncate">
                    {photo.description}
                  </p>
                </div>
              )}
              {/* Delete button */}
              {showEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPhotoToDelete(photo);
                  }}
                  className="absolute top-1 right-1 size-6 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"
                >
                  {deletingId === photo.id ? (
                    <Loader2 className="size-3 animate-spin text-white" />
                  ) : (
                    <Trash2 className="size-3 text-white" />
                  )}
                </button>
              )}
              {/* Edit/Annotate button */}
              {showEdit && isImage(photo) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnnotatorPhoto({
                      imageName: photo.imageName,
                      tags: photo.tags || [],
                      description: photo.description || '',
                      fileId: photo.id.startsWith('flex-photo-')
                        ? undefined
                        : photo.id,
                      type: photo.type,
                    });
                    setShowPhotoAnnotator(true);
                  }}
                  className="absolute top-1 right-8 size-6 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary"
                >
                  <Pencil className="size-3 text-white" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Other files (PDFs, videos) */}
      {otherFiles.length > 0 && (
        <div className="space-y-2">
          {otherFiles.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer group"
              onClick={() => handleFileClick(file, 0)}
            >
              <FileText className="size-5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">
                  {file.description || file.imageName || 'Document'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {file.type || 'File'}
                </p>
              </div>
              {showEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPhotoToDelete(file);
                  }}
                  className="size-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/10"
                >
                  {deletingId === file.id ? (
                    <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                  ) : (
                    <Trash2 className="size-3.5 text-muted-foreground hover:text-destructive" />
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lightbox dialog */}
      {lightboxIndex !== null && (
        <Dialog open={true} onOpenChange={() => setLightboxIndex(null)}>
          <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden">
            <div className="relative w-full h-full flex items-center justify-center bg-black min-h-[60vh]">
              <button
                onClick={() => setLightboxIndex(null)}
                className="absolute top-3 right-3 z-10 size-8 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80"
              >
                <X className="size-4 text-white" />
              </button>
              {imagePhotos[lightboxIndex]?.url && (
                <img
                  src={imagePhotos[lightboxIndex].url}
                  alt={imagePhotos[lightboxIndex].description || 'Photo'}
                  className="max-w-full max-h-[85vh] object-contain"
                />
              )}
              {/* Navigation arrows */}
              {imagePhotos.length > 1 && (
                <>
                  <button
                    onClick={() =>
                      setLightboxIndex(
                        (lightboxIndex - 1 + imagePhotos.length) %
                          imagePhotos.length
                      )
                    }
                    className="absolute left-3 top-1/2 -translate-y-1/2 size-10 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 text-white text-lg"
                  >
                    ‹
                  </button>
                  <button
                    onClick={() =>
                      setLightboxIndex((lightboxIndex + 1) % imagePhotos.length)
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 size-10 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 text-white text-lg"
                  >
                    ›
                  </button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* PDF viewer dialog */}
      {pdfUrl && (
        <Dialog open={true} onOpenChange={() => setPdfUrl(null)}>
          <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden">
            <div className="relative w-full h-[85vh]">
              <button
                onClick={() => setPdfUrl(null)}
                className="absolute top-3 right-3 z-10 size-8 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80"
              >
                <X className="size-4 text-white" />
              </button>
              <iframe
                src={pdfUrl}
                className="w-full h-full border-none"
                title="PDF Viewer"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Video viewer dialog */}
      {videoUrl && (
        <Dialog open={true} onOpenChange={() => setVideoUrl(null)}>
          <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden">
            <div className="relative w-full flex items-center justify-center bg-black min-h-[60vh]">
              <button
                onClick={() => setVideoUrl(null)}
                className="absolute top-3 right-3 z-10 size-8 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80"
              >
                <X className="size-4 text-white" />
              </button>
              <video
                src={videoUrl}
                controls
                className="max-w-full max-h-[85vh]"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Empty state when no photos after filtering */}
      {filteredPhotos.length === 0 && photos.length > 0 && (
        <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
          <p className="text-sm">No photos match the selected filter.</p>
        </div>
      )}

      {photos.length === 0 && !showEdit && (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <ImagePlus className="size-10 mb-2" />
          <p className="text-sm">No photos yet.</p>
        </div>
      )}

      {/* Delete confirmation modal */}
      <ConfirmModal
        open={!!photoToDelete}
        onClose={() => setPhotoToDelete(null)}
        onConfirm={async () => {
          if (photoToDelete) {
            await handleDelete(photoToDelete);
            setPhotoToDelete(null);
          }
        }}
        title="Delete file"
        description="Are you sure you want to delete this file? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        isLoading={!!deletingId}
      />

      {/* Photo Annotator Modal */}
      <PhotoAnnotator
        open={showPhotoAnnotator}
        onClose={() => {
          setShowPhotoAnnotator(false);
          setAnnotatorPhoto(null);
        }}
        photo={annotatorPhoto}
        dealId={deal?.id || ''}
        onSave={() => {
          onTriggerSelectedDealRefetch?.();
        }}
      />
    </div>
  );
}

function DealNotesTab({
  comments,
  dealId,
  categoryId,
  currentOwnerID,
  showEdit = true,
  onRefetch,
}: {
  comments: DealComment[];
  dealId: string;
  categoryId?: string;
  currentOwnerID?: string;
  showEdit?: boolean;
  onRefetch?: () => void;
}) {
  const { success: toastSuccess, error: toastError } = useToast();
  const [newNote, setNewNote] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  const handleSubmitNote = async () => {
    if (!newNote.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const now = new Date();
      const completeMissionInput = {
        currentOwnerID: currentOwnerID || '',
        userMissionID: '',
        completedFlag: false,
        myDoneActionCount: 1,
        doneImage: '',
        doneImageType: '',
        status: 'completed',
        extID: '',
        comments: newNote.trim(),
        dealID: dealId,
        dispositionID: '',
        followupTZ: getTZ(),
        contactType: '',
        categoryID: categoryId || '',
        tmst: now.toISOString(),
      };

      const res = (await API.graphql({
        query: mutations.completeMission,
        variables: { missionInfo: JSON.stringify(completeMissionInput) },
      })) as { data: { completeMission: string } };

      const status = JSON.parse(res.data.completeMission);
      if (status.status === 'success') {
        setNewNote('');
        setIsFocused(false);
        toastSuccess('Note added successfully');
        onRefetch?.();
      } else {
        toastError('Failed to add note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      toastError('Failed to add note');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Add note form */}
      {showEdit && (
        <div className="space-y-2">
          <textarea
            placeholder="Add a note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onFocus={() => setIsFocused(true)}
            rows={isFocused ? 3 : 1}
            className="w-full px-3 py-2.5 text-sm rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
          />
          {isFocused && (
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsFocused(false);
                  setNewNote('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmitNote}
                disabled={!newNote.trim() || isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="size-3.5 animate-spin mr-1.5" />
                ) : null}
                Save
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Notes list */}
      {comments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <MessageSquare className="size-10 mb-2" />
          <p className="text-sm">No notes yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="p-3 rounded-lg border border-border bg-card"
            >
              {/* Header: avatar + name on left, date on right */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2.5">
                  {comment.user.profilePicture ? (
                    <img
                      src={comment.user.profilePicture}
                      alt={comment.user.name}
                      className="size-9 rounded-full object-cover"
                    />
                  ) : (
                    <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary">
                        {comment.user.initials}
                      </span>
                    </div>
                  )}
                  <span className="text-sm font-bold text-foreground">
                    {comment.user.name}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  Created:{' '}
                  {new Date(comment.createdAt).toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                  })}
                </span>
              </div>

              {/* Disposition badge */}
              {comment.dispositionTitle && (
                <span className="inline-block text-[10px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full mb-2">
                  {comment.dispositionTitle}
                </span>
              )}

              {/* Comment text */}
              <p className="text-sm text-foreground whitespace-pre-wrap pl-1">
                {comment.comment}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DealAppointmentsTab({
  appointments,
}: {
  appointments: DealAppointment[];
}) {
  if (!appointments.length) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Calendar className="size-10 mb-2" />
        <p className="text-sm">No appointments yet.</p>
      </div>
    );
  }

  function getStatusClasses(status: string): string {
    switch (status.toLowerCase()) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'completed':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
      case 'cancelled':
      case 'customer cancelled':
      case 'technician cancelled':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      case 'rescheduled':
      case 'customer rescheduled':
      case 'technician rescheduled':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      default:
        return 'bg-muted text-muted-foreground';
    }
  }

  return (
    <div className="space-y-3">
      {appointments.map((appt, i) => (
        <div
          key={appt.id || i}
          className="p-3 rounded-lg border border-border bg-card"
        >
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="size-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {formatScheduleDate(
                appt.appointmentDate,
                appt.appointmentStartTime
              )}
            </span>
          </div>
          <div className="space-y-1 text-xs text-muted-foreground">
            {appt.deal?.name && <p>{appt.deal.name}</p>}
            {appt.user?.name && <p>Assigned to {appt.user.name}</p>}
            {appt.setter?.name && (
              <p>
                {getDealOwnerConfig().setterLabel}: {appt.setter.name}
              </p>
            )}
          </div>
          <div className="mt-2">
            <span
              className={cn(
                'inline-block text-[10px] font-medium px-2 py-0.5 rounded-full',
                getStatusClasses(appt.status)
              )}
            >
              {appt.status}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Contracts Tab ───────────────────────────────────────────

function DealContractsTab({ deal }: { deal: any }) {
  const contracts = React.useMemo(() => {
    if (!deal?.flexAttributes) {
      return [];
    }
    try {
      const flexAttrs =
        typeof deal.flexAttributes === 'string'
          ? JSON.parse(deal.flexAttributes)
          : deal.flexAttributes;

      const docusealData = flexAttrs?.docuseal;
      if (!docusealData) {
        return [];
      }

      const result: {
        productFamily: string;
        events: {
          eventType: string;
          role: string;
          status: string;
          timestamp: string;
          documentUrl?: string;
        }[];
        lastUpdated: string;
      }[] = [];

      Object.keys(docusealData).forEach((productFamily) => {
        const contractData = docusealData[productFamily];
        let events: any[] = [];

        if (contractData.history && Array.isArray(contractData.history)) {
          events = contractData.history.map((h: any) => ({
            eventType: h.event_type || 'contract_status',
            role: h.role || '',
            status: h.status || '',
            timestamp: h.timestamp || '',
            documentUrl: h.documentUrl || h.documentUrls?.[0],
          }));
        } else if (contractData.role && contractData.status) {
          events.push({
            eventType: 'contract_status',
            role: contractData.role,
            status: contractData.status,
            timestamp: contractData.timestamp || '',
            documentUrl: contractData.documentUrl,
          });
        }

        events.sort(
          (a: any, b: any) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

        if (events.length > 0) {
          result.push({
            productFamily,
            events,
            lastUpdated: contractData.timestamp || events[0]?.timestamp || '',
          });
        }
      });

      return result.sort(
        (a, b) =>
          new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
      );
    } catch {
      return [];
    }
  }, [deal]);

  if (contracts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <FileText className="size-10 mb-2" />
        <p className="text-sm">No contract history available.</p>
      </div>
    );
  }

  function getStatusClasses(status: string): string {
    switch (status) {
      case 'completed':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
      case 'created':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'sent':
      case 'opened':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      default:
        return 'bg-muted text-muted-foreground';
    }
  }

  function formatEventDesc(event: any): string {
    const role = event.role.charAt(0).toUpperCase() + event.role.slice(1);
    switch (event.eventType) {
      case 'form.completed':
        return `${role} completed contract`;
      case 'form.opened':
        return `${role} viewed contract`;
      case 'form.sent':
        return `Contract sent to ${role}`;
      case 'form.created':
        return 'Contract created';
      default:
        return `${role} ${event.status}`;
    }
  }

  return (
    <div className="space-y-3">
      {contracts.map((contract, i) => (
        <details key={i} open={i === 0} className="group">
          <summary className="flex items-center justify-between p-3 rounded-lg border border-border bg-card cursor-pointer hover:bg-muted/50 transition-colors list-none">
            <div>
              <p className="text-sm font-semibold text-foreground">
                {contract.productFamily.charAt(0).toUpperCase() +
                  contract.productFamily.slice(1)}{' '}
                Contract
              </p>
              <p className="text-xs text-muted-foreground">
                Last updated:{' '}
                {new Date(contract.lastUpdated).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>
            <span className="text-muted-foreground text-xs group-open:rotate-180 transition-transform">
              ▼
            </span>
          </summary>
          <div className="mt-2 ml-2 border-l-2 border-border pl-4 space-y-3">
            {contract.events.map((event: any, j: number) => (
              <div key={j} className="flex items-start gap-2">
                <span
                  className={cn(
                    'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full mt-0.5 shrink-0',
                    getStatusClasses(event.status)
                  )}
                >
                  {event.status}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground">
                    {formatEventDesc(event)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(event.timestamp).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </p>
                  {event.documentUrl && (
                    <a
                      href={event.documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-primary hover:underline"
                    >
                      View document
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}

// ── Lead Info Tab ───────────────────────────────────────────

function formatLabel(key: string): string {
  // Convert camelCase or snake_case to Title Case
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function DealLeadInfoTab({ deal }: { deal: any }) {
  const leadAttributes = React.useMemo(() => {
    if (!deal) {
      return [];
    }
    const attrs: { label: string; value: string }[] = [];
    try {
      const flexAttrs = deal.flexAttributes
        ? typeof deal.flexAttributes === 'string'
          ? JSON.parse(deal.flexAttributes)
          : deal.flexAttributes
        : {};

      Object.entries(flexAttrs).forEach(([key, value]) => {
        if (
          value &&
          typeof value === 'string' &&
          value.trim() &&
          key !== 'photo' &&
          key !== 'pictures' &&
          key !== 'docuseal'
        ) {
          attrs.push({ label: formatLabel(key), value: value as string });
        }
      });

      if (deal.name && !attrs.find((a) => a.label === 'Name')) {
        attrs.unshift({ label: 'Name', value: deal.name });
      }
      if (deal.description && !attrs.find((a) => a.label === 'Description')) {
        attrs.push({ label: 'Description', value: deal.description });
      }
      if (deal.stage?.title) {
        attrs.push({ label: 'Stage', value: deal.stage.title });
      }
      if (deal.value) {
        attrs.push({
          label: 'Value',
          value: `$${Number(deal.value).toLocaleString()}`,
        });
      }
    } catch (e) {
      console.error('Error parsing flexAttributes:', e);
    }
    return attrs;
  }, [deal]);

  if (leadAttributes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Contact className="size-10 mb-2" />
        <p className="text-sm">No lead information available.</p>
      </div>
    );
  }

  // Split into pairs for 2-column layout matching Figma
  const rows: { label: string; value: string }[][] = [];
  for (let i = 0; i < leadAttributes.length; i += 2) {
    rows.push(leadAttributes.slice(i, i + 2));
  }

  return (
    <div className="space-y-6">
      {/* Lead Overview header */}
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-bold text-foreground tracking-tight">
            Lead Overview
          </h3>
          {deal?.stage?.title && (
            <p className="text-[13px] text-muted-foreground">
              {deal.stage.title}
            </p>
          )}
        </div>
        {deal?.disposition?.title &&
          deal.disposition.title !== 'No Disposition' && (
            <span className="inline-flex items-center text-xs font-bold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 px-3 py-1.5 rounded-full">
              {deal.disposition.title}
            </span>
          )}
      </div>

      {/* Lead details card */}
      <div className="border border-border rounded-2xl overflow-hidden">
        <div className="flex items-center gap-2 p-3 border-b border-border">
          <Contact className="size-5 text-muted-foreground" />
          <span className="text-sm font-bold text-muted-foreground flex-1">
            Lead details
          </span>
        </div>
        <div className="px-3 py-4 space-y-3">
          {rows.map((row, rowIdx) => (
            <div key={rowIdx} className="flex gap-2">
              {row.map((attr, colIdx) => (
                <div key={colIdx} className="flex-1 min-w-0 space-y-0.5">
                  <p className="text-xs text-muted-foreground leading-5">
                    {attr.label}
                  </p>
                  <p className="text-[15px] text-foreground leading-5 break-words">
                    {attr.value}
                  </p>
                </div>
              ))}
              {row.length === 1 && <div className="flex-1" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Door Data Tab ───────────────────────────────────────────

function DealDoorDataTab({ deal }: { deal: any }) {
  const [addressData, setAddressData] = React.useState<any>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const useDoorData = (global as any)?.appSettings?.features?.route
    ?.useDoorData;
  const noDoorData = (global as any)?.appSettings?.features?.route?.noDoorData;
  const enhancedDoorData = (global as any)?.appSettings?.features?.route
    ?.enhancedDoorData;
  const showDistanceFromDoor = (global as any)?.appSettings?.features?.route
    ?.showDistanceFromDoor;

  const dealFlex = React.useMemo(() => {
    if (!deal?.flexAttributes) {
      return {};
    }
    try {
      return typeof deal.flexAttributes === 'string'
        ? JSON.parse(deal.flexAttributes)
        : deal.flexAttributes;
    } catch {
      return {};
    }
  }, [deal?.flexAttributes]);

  const addrFlex = React.useMemo(() => {
    if (!addressData?.flexAttributes) {
      return {};
    }
    try {
      return typeof addressData.flexAttributes === 'string'
        ? JSON.parse(addressData.flexAttributes)
        : addressData.flexAttributes;
    } catch {
      return {};
    }
  }, [addressData?.flexAttributes]);

  React.useEffect(() => {
    async function fetchAddress() {
      if (!deal?.id) {
        return;
      }
      setIsLoading(true);
      try {
        const query = `
          query GetAddress($id: ID!) {
            getAddress(id: $id) {
              id
              street
              address
              city
              region
              postalCode
              distanceFromDoor
              creditEstimate
              age
              income
              lengthOfResidence
              flexAttributes
            }
          }
        `;
        const result: any = await API.graphql(
          graphqlOperation(query, { id: deal.id })
        );
        if (result?.data?.getAddress) {
          setAddressData(result.data.getAddress);
        }
      } catch (err) {
        console.error('Error fetching address for door data:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchAddress();
  }, [deal?.id]);

  // Build full address string (must be before early returns to satisfy hook rules)
  const addr = addressData || {};
  const fullAddress = React.useMemo(() => {
    const parts = [
      dealFlex.Address || dealFlex.address || addr.address || addr.street,
      dealFlex.City || dealFlex.city || addr.city,
      dealFlex.State || dealFlex.state || addr.region,
      dealFlex.ZipCode || dealFlex.zipCode || dealFlex.zip || addr.postalCode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  }, [dealFlex, addr]);

  if (noDoorData || !useDoorData) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Building2 className="size-10 mb-2" />
        <p className="text-sm">Door data not enabled.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const pick = (...keys: string[]) => {
    for (const k of keys) {
      if (addr[k]) {
        return addr[k];
      }
      if (addrFlex[k]) {
        return addrFlex[k];
      }
      if (dealFlex[k]) {
        return dealFlex[k];
      }
    }
    return null;
  };

  // Weather events from address flexAttributes
  let weatherInfo: any[] = [];
  if (featureFlag('weatherOverlay')) {
    if (Array.isArray(addrFlex?.weather_events)) {
      weatherInfo = addrFlex.weather_events;
    }
  }

  const metersToFeet = (meters: number): number => Math.round(meters * 3.28084);

  const decodeEstimatedCreditRating = (ratingCode: string | null): string => {
    if (ratingCode === 'A') {
      return '800+';
    }
    if (ratingCode === 'B') {
      return '750-799';
    }
    if (ratingCode === 'C') {
      return '700-749';
    }
    if (ratingCode === 'D') {
      return '650-699';
    }
    if (ratingCode === 'E') {
      return '600-649';
    }
    if (ratingCode === 'F') {
      return '550-599';
    }
    if (ratingCode === 'G') {
      return '500-549';
    }
    if (ratingCode === 'H') {
      return '< 499';
    }
    return '?';
  };

  const formatIncome = (val: string | null): string => {
    if (!val || val === '') {
      return '';
    }
    const num = Number(val);
    if (isNaN(num)) {
      return val;
    }
    return '$' + Math.round(num).toLocaleString();
  };

  const formatHomeValue = (val: string | null): string => {
    if (!val || val === '') {
      return '';
    }
    const num = Number(val);
    if (isNaN(num)) {
      return val;
    }
    return '$' + Math.round(num).toLocaleString();
  };

  const lengthOfResidence = pick('lengthOfResidence');
  const residenceLengthDisplay = (): string => {
    if (!lengthOfResidence) {
      return '';
    }
    const val = Number(lengthOfResidence);
    if (isNaN(val)) {
      return String(lengthOfResidence);
    }
    if (val > 1) {
      return `${val} years`;
    }
    return '1 year or less';
  };

  const lastSoldDisplay = (): string => {
    if (!lengthOfResidence) {
      return '';
    }
    const val = Number(lengthOfResidence);
    if (isNaN(val) || val === 0) {
      return '';
    }
    return String(new Date().getFullYear() - val);
  };

  const rows: { label: string; value: string }[][] = enhancedDoorData
    ? [
        [
          {
            label: 'Est. Credit Rating',
            value: decodeEstimatedCreditRating(
              pick('estimatedAreaCreditRating', 'creditEstimate')
            ),
          },
          {
            label: 'Income',
            value: formatIncome(pick('income')) || '',
          },
        ],
        [
          {
            label: 'Home Value',
            value: formatHomeValue(pick('medianHomeValue')) || '',
          },
          {
            label: 'SqFt',
            value: pick('homeSquareFootage')
              ? Number(pick('homeSquareFootage')).toLocaleString()
              : '',
          },
        ],
        [
          {
            label: 'Year built',
            value: pick('homeBuiltYear') || '',
          },
          {
            label: 'Last sold',
            value: lastSoldDisplay(),
          },
        ],
        [
          {
            label: 'Residence Length',
            value: residenceLengthDisplay(),
          },
          {
            label: 'Owner age',
            value: pick('age') ? String(pick('age')) : '',
          },
        ],
      ]
    : [];

  const hasAnyData = rows.some((row) =>
    row.some((cell) => cell.value !== '' && cell.value !== '?')
  );

  if (!enhancedDoorData && !fullAddress) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Building2 className="size-10 mb-2" />
        <p className="text-sm">No door data available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Property Address */}
      {fullAddress && (
        <div className="p-3 rounded-lg border border-border bg-card">
          <p className="text-xs text-muted-foreground mb-0.5">
            Property Address
          </p>
          <p className="text-sm font-medium text-foreground">{fullAddress}</p>
        </div>
      )}

      {/* Weather Events Table */}
      {weatherInfo.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <CloudRain className="size-4 text-muted-foreground" />
            <span className="text-sm font-bold text-foreground">
              Weather Events Detected
            </span>
          </div>
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="grid grid-cols-3 gap-0 bg-muted px-3 py-2 border-b border-border">
              <span className="text-xs font-semibold text-foreground">
                Date
              </span>
              <span className="text-xs font-semibold text-foreground">
                Event
              </span>
              <span className="text-xs font-semibold text-foreground">
                Hail Size
              </span>
            </div>
            {weatherInfo.map((event: any, i: number) => (
              <div
                key={i}
                className={cn(
                  'grid grid-cols-3 gap-0 px-3 py-2',
                  i < weatherInfo.length - 1 && 'border-b border-border',
                  i % 2 === 0 ? 'bg-muted/30' : 'bg-background'
                )}
              >
                <span className="text-xs text-foreground">
                  {event.event_date || 'N/A'}
                </span>
                <span className="text-xs text-foreground">
                  {event.event_type || 'N/A'}
                </span>
                <span className="text-xs text-foreground">
                  {event.hail_size ? `${event.hail_size}"` : 'N/A'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Household data */}
      {enhancedDoorData && hasAnyData && (
        <div className="border border-border rounded-2xl overflow-hidden">
          <div className="flex items-center gap-2 p-3 border-b border-border">
            <Building2 className="size-5 text-muted-foreground" />
            <span className="text-sm font-bold text-muted-foreground flex-1">
              Door Data
            </span>
          </div>
          <div className="p-3 space-y-2">
            {rows.map((row, i) => (
              <div key={i} className="flex justify-between gap-4">
                {row.map((cell, j) => (
                  <div key={j} className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-foreground">
                      {cell.label}:
                    </span>{' '}
                    <span className="text-sm text-muted-foreground">
                      {cell.value || ''}
                    </span>
                  </div>
                ))}
              </div>
            ))}
            {/* Distance from door */}
            {showDistanceFromDoor && addr.distanceFromDoor != null && (
              <div className="flex justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-foreground">
                    Distance from door:
                  </span>{' '}
                  <span className="text-sm text-muted-foreground">
                    {addr.distanceFromDoor !== ''
                      ? `${metersToFeet(Number(addr.distanceFromDoor))} ft`
                      : '?'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Status Tab ──────────────────────────────────────────────

function DealStatusTab({
  deal,
  onStagePress,
}: {
  deal: any;
  onStagePress?: () => void;
}) {
  if (!deal) {
    return null;
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) {
      return 'N/A';
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const dealTags =
    deal?.tags?.items?.map((t: any) => ({
      id: t.tag?.id,
      name: t.tag?.name,
      color: t.tag?.color,
    })) || [];

  const rows: { label: string; value: React.ReactNode }[] = [];

  if (deal?.stage?.title) {
    rows.push({
      label: 'Stage',
      value: (
        <span
          className={cn(
            'inline-block text-xs font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full',
            onStagePress &&
              'cursor-pointer hover:bg-primary/20 transition-colors'
          )}
          onClick={onStagePress}
        >
          {deal.stage.title}
        </span>
      ),
    });
  }

  if (deal?.lastVisited) {
    rows.push({ label: 'Last Visit', value: formatDate(deal.lastVisited) });
  }

  if (deal?.lastVisitedByName) {
    rows.push({ label: 'Visited By', value: deal.lastVisitedByName });
  }

  if (
    deal?.disposition?.title &&
    deal.disposition.title !== 'No Disposition' &&
    deal.disposition.title !== 'Multi-Unit'
  ) {
    rows.push({ label: 'Disposition', value: deal.disposition.title });
  }

  if (rows.length === 0 && dealTags.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <ListChecks className="size-10 mb-2" />
        <p className="text-sm">No status information available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-0 rounded-lg bg-muted/50 divide-y divide-border">
      {rows.map((row, i) => (
        <div key={i} className="flex items-center justify-between px-3 py-2.5">
          <span className="text-sm font-medium text-foreground">
            {row.label}
          </span>
          <span className="text-sm text-foreground">{row.value}</span>
        </div>
      ))}
      {dealTags.length > 0 && (
        <div className="px-3 py-2.5">
          <span className="text-sm font-medium text-foreground block mb-1.5">
            Tags
          </span>
          <div className="flex flex-wrap gap-1.5">
            {dealTags.map((tag: any) => (
              <span
                key={tag.id}
                className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: tag.color ? `${tag.color}20` : undefined,
                  color: tag.color || undefined,
                }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Claim Tab ───────────────────────────────────────────────

function DealClaimTab({
  dealId,
  deal,
  pollTick,
  onOpenChat,
}: {
  dealId: string;
  deal: any;
  pollTick?: number;
  onOpenChat?: (dealId: string, taskListId: string) => void;
}) {
  const ClaimTabV2 = React.lazy(() => import('./ClaimTab'));

  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      }
    >
      <ClaimTabV2
        dealID={dealId}
        dealInfo={deal}
        pollTick={pollTick}
        onOpenChat={onOpenChat}
      />
    </React.Suspense>
  );
}

// ── Reviews Tab ─────────────────────────────────────────────

function DealReviewsTab() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
      <Star className="size-10 mb-2" />
      <p className="text-sm">Review display coming soon.</p>
    </div>
  );
}
